import * as React from 'react'
import LoadingIcon from '../../../assets/img/Loading/Loading.svg'
import './securitiesReportIndicators.css'
import ConsolidatedCompanyTable from './components/ConsolidatedCompanyTable'
import CompanyStatusSection from './components/CompanyStatusSection'
import DisplayTargetBar from './components/DisplayTargetBar'
import DiversityTable from './components/DiversityTable'
import { exportEmployeeStatusToExcel } from './extra/exportToExcel'
import {
  EMPLOYEE_STATUS_MESSAGES,
  filterSubmitterRows,
  fetchEmployeeStatusData,
  hasEmployeeStatusDisplayData,
  resolveEmployeeStatusErrorMessage,
  sortSubmitterRows
} from './extra/services'
import { fiscalPeriodToQsYear, resolveFiscalPeriodOption } from './extra/periodSync'

const AGGREGATION_PERIOD_NOTE = {
  title: '【集計期間およびデータ作成に関する注記】',
  paragraphs: [
    '本集計では、提出会社と子会社の決算期（計算期間）が異なる場合、提出会社の決算期に合わせて自動集計を行っています。',
    '提出会社と集計期間が異なる子会社の「従業員数情報」または「ジェンダー統計情報」をアップロードして集計対象としている場合は、有価証券報告書の該当部にその旨の注記を記載することを推奨いたします。'
  ]
}

/**
 * 10.6.1 有報開示指標（従業員の状況）
 *
 * @param {object} props QuickSight から渡される props
 */

const SecuritiesReportEmployeeStatus = (props) => {
  const [selectedYear, setSelectedYear] = React.useState('')
  const [yearOptions, setYearOptions] = React.useState([])
  const [salaryUnit, setSalaryUnit] = React.useState('thousand-yen')
  const [employeeSortDir, setEmployeeSortDir] = React.useState(null)
  const [yearData, setYearData] = React.useState(null)
  const [submitterRows, setSubmitterRows] = React.useState([])
  const [dataByPeriod, setDataByPeriod] = React.useState({})
  const [showConsolidatedSection, setShowConsolidatedSection] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(true)
  const [errorMessage, setErrorMessage] = React.useState('')

  const applyYearData = React.useCallback((fiscalPeriod, source, resetSort = true) => {
    const data = source[fiscalPeriod]
    if (!data) {
      return
    }
    const filtered = filterSubmitterRows(data.submitter)
    setYearData(data)
    setSubmitterRows(filtered)
    if (resetSort) {
      setEmployeeSortDir(null)
    }
  }, [])

  const corporationNumber = props.corporationNumber
  const isParent = props.isParent
  const accPeriod = props.accPeriod
  const selectedFiscalYear = props.selectedFiscalYear
  const onSelectedFiscalYearChange = props.onSelectedFiscalYearChange

  const syncQsYear = React.useCallback(
    (fiscalPeriod) => {
      if (typeof onSelectedFiscalYearChange !== 'function') {
        return
      }
      const qsYear = fiscalPeriodToQsYear(fiscalPeriod, accPeriod)
      if (qsYear) {
        onSelectedFiscalYearChange(qsYear)
      }
    },
    [accPeriod, onSelectedFiscalYearChange]
  )

  const loadAllData = React.useCallback(async () => {
    setIsLoading(true)
    setErrorMessage('')
    try {
      const {
        yearOptions: options,
        dataByPeriod: nextDataByPeriod,
        showConsolidatedSection: nextShowConsolidatedSection
      } = await fetchEmployeeStatusData({ corporationNumber, isParent })
      if (!options.length) {
        setErrorMessage(EMPLOYEE_STATUS_MESSAGES.fetchFailed)
        setYearOptions([])
        setDataByPeriod({})
        setYearData(null)
        setSubmitterRows([])
        setShowConsolidatedSection(false)
        return
      }

      setYearOptions(options)
      setDataByPeriod(nextDataByPeriod)
      setShowConsolidatedSection(nextShowConsolidatedSection)
      // QuickSight の年度（10.6.2 等）と揃える。無ければ先頭
      const initialYear = resolveFiscalPeriodOption(options, selectedFiscalYear, accPeriod)
      setSelectedYear(initialYear)
      applyYearData(initialYear, nextDataByPeriod)
    } catch (error) {
      setErrorMessage(resolveEmployeeStatusErrorMessage(error))
      setYearOptions([])
      setDataByPeriod({})
      setYearData(null)
      setSubmitterRows([])
      setShowConsolidatedSection(false)
    } finally {
      setIsLoading(false)
    }
  }, [accPeriod, applyYearData, corporationNumber, isParent, selectedFiscalYear])

  React.useEffect(() => {
    loadAllData()
    // マウント時のみ（タブ復帰で再マウント）。selectedFiscalYear 変更での再取得はしない
    // eslint-disable-next-line react-hooks/exhaustive-deps -- init on mount only
  }, [])

  const handleYearChange = (year) => {
    setSelectedYear(year)
    applyYearData(year, dataByPeriod)
    syncQsYear(year)
  }

  const handleSortByEmployees = () => {
    const nextDir = employeeSortDir === 'asc' ? 'desc' : 'asc'
    setEmployeeSortDir(nextDir)
    setSubmitterRows((rows) => sortSubmitterRows(rows, nextDir))
  }

  const handleResetSort = () => {
    if (!yearData) return
    setEmployeeSortDir(null)
    setSubmitterRows(filterSubmitterRows(yearData.submitter))
  }

  const handleExport = async () => {
    if (!yearData || !hasEmployeeStatusDisplayData(yearData, showConsolidatedSection)) {
      return
    }
    await exportEmployeeStatusToExcel(yearData, submitterRows, salaryUnit, selectedYear, {
      hideConsolidated: !showConsolidatedSection
    })
  }

  const canExport = hasEmployeeStatusDisplayData(yearData, showConsolidatedSection)

  if (isLoading) {
    return (
      <div className="securities-report-indicators securities-report-indicators--status">
        <img src={LoadingIcon} alt="LoadingIcon" />
      </div>
    )
  }

  if (errorMessage || !yearData) {
    return (
      <div className="securities-report-indicators securities-report-indicators--empty">
        <DisplayTargetBar
          yearOptions={yearOptions}
          selectedYear={selectedYear}
          onYearChange={handleYearChange}
          onExport={handleExport}
          exportDisabled
          yearSelectDisabled
        />
        <div className="securities-report-indicators__empty-body">
          <p className="securities-report-indicators__error">{errorMessage || EMPLOYEE_STATUS_MESSAGES.fetchFailed}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="securities-report-indicators">
      <DisplayTargetBar
        yearOptions={yearOptions}
        selectedYear={selectedYear}
        onYearChange={handleYearChange}
        onExport={handleExport}
        exportDisabled={!canExport}
      />

      <div className="flex justify-between items-center mb-8 px-12">
        <h2 className="text-base font-semibold m-0 p-0">【従業員の状況】</h2>
        <span className="text-base">{yearData.snapshotDate}</span>
      </div>

      {showConsolidatedSection ? (
        <ConsolidatedCompanyTable rows={yearData.consolidated.rows} total={yearData.consolidated.total} />
      ) : null}
      <CompanyStatusSection
        submitterRows={submitterRows}
        segmentRows={yearData.segmentBreakdown.rows}
        segmentTotal={yearData.segmentBreakdown.total}
        salaryUnit={salaryUnit}
        onSalaryUnitChange={setSalaryUnit}
        employeeSortDir={employeeSortDir}
        onSortByEmployees={handleSortByEmployees}
        onResetSort={handleResetSort}
      />
      <DiversityTable rows={yearData.diversity} genderLabels={yearData.genderLabels} />
      <div className="securities-report-indicators__footnote px-12">
        <p className="m-0 font-bold">{AGGREGATION_PERIOD_NOTE.title}</p>
        {AGGREGATION_PERIOD_NOTE.paragraphs.map((paragraph) => (
          <p key={paragraph} className="m-0 mt-2">
            {paragraph}
          </p>
        ))}
      </div>
    </div>
  )
}

export default SecuritiesReportEmployeeStatus
